import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckIcon, MinusIcon, ShieldAlertIcon, ShieldCheckIcon } from 'lucide-react';
import { AdminMetricCard, AdminPageTitle, ConfirmationModal, Panel, PermissionGate, StatusBadge, UserCell } from '../../components/admin/AdminPrimitives';
import { DataTable, type Column } from '../../components/admin/DataTable';
import { Button } from '../../components/ui/Button';
import { permissionAreas, permissionLabels, highImpactActions, permissionMatrix, recordAudit, roleLabels, sensitiveHealthGrants, canIssueSensitiveHealthGrant, grantSensitiveHealthAccess, revokeSensitiveHealthAccess, team, type SensitiveHealthGrant, type Permission, type TeamMember, type TeamRole } from '../../data/admin';
import { capabilityLabels, capabilityMatrix, capabilityOrder, prototypeActorName, prototypeAdminRole } from '../../data/adminOperations';
import { useLocale } from '../../contexts/LocaleContext';
import type { L10n } from '../../types';
const statusLabels: Record<string, string> = {
  active: 'Ativo',
  invited: 'Convidado',
  suspended: 'Suspenso'
};
type TeamAction = 'role' | 'suspend' | 'reactivate' | 'remove' | 'twoFactor';

/** Every action that changes administrative access: confirmation + audit. */
const teamActionCopy: Record<TeamAction, {
  title: string;
  body: (name: string) => string;
  confirm: string;
  destructive: boolean;
  audit: L10n;
}> = {
  role: {
    title: 'Alterar o papel deste membro?',
    body: name => `O novo papel de ${name} substitui todas as permissões do papel atual.`,
    confirm: 'Alterar papel',
    destructive: true,
    audit: {
      'pt-BR': 'Alterou papel de membro',
      'en-US': 'Changed member role'
    }
  },
  suspend: {
    title: 'Suspender o acesso deste membro?',
    body: name => `${name} perde o acesso ao Admin e todas as sessões ativas são encerradas.`,
    confirm: 'Suspender acesso',
    destructive: true,
    audit: {
      'pt-BR': 'Suspendeu acesso de membro',
      'en-US': 'Suspended member access'
    }
  },
  reactivate: {
    title: 'Reativar o acesso deste membro?',
    body: name => `${name} volta a acessar o Admin com o papel que já tinha.`,
    confirm: 'Reativar acesso',
    destructive: false,
    audit: {
      'pt-BR': 'Reativou acesso de membro',
      'en-US': 'Reactivated member access'
    }
  },
  remove: {
    title: 'Remover este membro da equipe?',
    body: name => `${name} deixa a equipe e perde qualquer acesso ao Admin. O histórico de auditoria é mantido.`,
    confirm: 'Remover membro',
    destructive: true,
    audit: {
      'pt-BR': 'Removeu membro da equipe',
      'en-US': 'Removed team member'
    }
  },
  twoFactor: {
    title: 'Redefinir o 2FA deste membro?',
    body: name => `O segundo fator atual de ${name} é revogado e um novo cadastro será exigido no próximo acesso.`,
    confirm: 'Redefinir 2FA',
    destructive: true,
    audit: {
      'pt-BR': 'Redefiniu 2FA de membro',
      'en-US': 'Reset member 2FA'
    }
  }
};
const roles: TeamRole[] = ['superadmin', 'admin', 'moderator', 'content', 'analyst', 'support'];
const permissions: Permission[] = ['view', 'create', 'edit', 'delete', 'moderate', 'export', 'manage'];
export function AdminTeam() {
  const {
    tl
  } = useLocale();
  const navigate = useNavigate();
  const [pending, setPending] = useState<{
    member: TeamMember;
    action: TeamAction;
  } | null>(null);
  const [overrides, setOverrides] = useState<Record<string, Partial<TeamMember>>>({});
  const rows = team.map(member => ({
    ...member,
    ...overrides[member.id]
  }));
  const ask = (action: TeamAction) => (member: TeamMember) => setPending({
    member,
    action
  });
  const confirmAction = () => {
    if (!pending) return;
    const {
      member,
      action
    } = pending;
    const copy = teamActionCopy[action];
    const before = action === 'role' ? member.role : action === 'twoFactor' ? member.twoFactor ? 'on' : 'off' : member.status;
    const after = action === 'suspend' ? 'suspended' : action === 'reactivate' ? 'active' : action === 'remove' ? 'removed' : action === 'twoFactor' ? 'reset' : 'pending review';
    if (action === 'suspend' || action === 'reactivate') {
      setOverrides(prev => ({
        ...prev,
        [member.id]: {
          ...prev[member.id],
          status: after as TeamMember['status']
        }
      }));
    }
    if (action === 'twoFactor') {
      setOverrides(prev => ({
        ...prev,
        [member.id]: {
          ...prev[member.id],
          twoFactor: false
        }
      }));
    }
    recordAudit({
      action: copy.audit,
      entity: `team:${member.id}`,
      context: {
        'pt-BR': `Equipe · ${member.name}`,
        'en-US': `Team · ${member.name}`
      },
      before,
      after,
      severity: 'sensitive'
    });
    setPending(null);
  };
  const columns: Column<TeamMember>[] = [{
    id: 'member',
    header: 'Membro',
    width: '28%',
    sortValue: row => row.name,
    cell: row => <UserCell user={{
      id: row.id,
      name: row.name,
      username: row.email
    }} secondary={row.email} data-id="element-2145" data-mp="19cvbnl6rqz15" />
  }, {
    id: 'role',
    header: 'Papel',
    sortValue: row => row.role,
    cell: row => <span className="text-[12.5px] font-medium text-deep" data-id="element-2146" data-mp="1s0xg7uwahbde">{tl(roleLabels[row.role])}</span>
  }, {
    id: 'twoFactor',
    header: '2FA',
    cell: row => row.twoFactor ? <span className="flex items-center gap-1 text-[11.5px] font-medium text-soft" data-id="element-2147" data-mp="1izz9h4ugupr4">
            <ShieldCheckIcon size={13} aria-hidden="true" data-id="element-2148" data-mp="14bcw38oeg82j" />
            Ativo
          </span> : <span className="flex items-center gap-1 text-[11.5px] font-medium text-gold-deep" data-id="element-2149" data-mp="191taav01nxpy">
            <ShieldAlertIcon size={13} aria-hidden="true" data-id="element-2150" data-mp="van0o631wjpc" />
            Pendente
          </span>
  }, {
    id: 'lastAccess',
    header: 'Último acesso',
    sortValue: row => row.lastAccess,
    cell: row => <span className="text-muted" data-id="element-2151" data-mp="lc7emhivs0mp">{row.lastAccess}</span>
  }, {
    id: 'status',
    header: 'Status',
    sortValue: row => row.status,
    cell: row => <StatusBadge status={row.status} label={statusLabels[row.status]} data-id="element-2152" data-mp="76ypww0fygn5" />
  }];
  return <div className="space-y-4" data-id="element-2153" data-mp="2g8k5ho3sbrx">
      <AdminPageTitle title="Equipe" subtitle="Membros com acesso ao admin, seus papéis e o estado do segundo fator." action={<>
            <Button variant="outline" onClick={() => navigate('/admin/papeis')} data-id="element-2155" data-mp="3ongfrs9814ar">
              Ver papéis
            </Button>
            <Button variant="gold" data-id="element-2156" data-mp="q62v1tov3q9j">Convidar membro</Button>
          </>} data-id="element-2154" data-mp="2fytdrv8i0l6" />

      <section className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-id="element-2157" data-mp="l66r1ug5zytz">
        <AdminMetricCard label="Membros ativos" value="6" data-id="element-2158" data-mp="l4vm3razgl1a" />
        <AdminMetricCard label="Moderadores" value="2" data-id="element-2159" data-mp="g6bwngx6kr46" />
        <AdminMetricCard label="2FA pendente" value="3" accent data-id="element-2160" data-mp="b8skcmk1moev" />
        <AdminMetricCard label="Convites abertos" value="1" data-id="element-2161" data-mp="b8acstrxknrn" />
      </section>

      <DataTable rows={rows} columns={columns} rowKey={row => row.id} pageSize={10} actions={[{
      id: 'role',
      label: 'Alterar papel',
      onSelect: ask('role')
    }, {
      id: 'permissions',
      label: 'Ver permissões',
      onSelect: () => navigate('/admin/papeis')
    }, {
      id: 'twoFactor',
      label: 'Redefinir / revogar 2FA',
      onSelect: ask('twoFactor')
    }, {
      id: 'reactivate',
      label: 'Reativar acesso',
      onSelect: ask('reactivate')
    }, {
      id: 'suspend',
      label: 'Suspender acesso',
      destructive: true,
      onSelect: ask('suspend')
    }, {
      id: 'remove',
      label: 'Remover membro',
      destructive: true,
      onSelect: ask('remove')
    }]} data-id="element-2162" data-mp="30yni9rcyaoe1" />

      <Panel title="Autorização" data-id="element-2163" data-mp="1z4qcmp8xv8i4">
        <p className="flex items-start gap-2 text-[12.5px] leading-relaxed text-muted" data-id="element-2164" data-mp="245ede2myz3pj">
          <ShieldCheckIcon size={15} className="mt-[1px] shrink-0 text-gold" aria-hidden="true" data-id="element-2165" data-mp="243l5kk0medfy" />
          A interface esconde o que o papel não pode usar, mas a autorização real é validada
          no backend em cada requisição. Ocultar elementos nunca é o mecanismo de segurança.
        </p>
      </Panel>

      <ConfirmationModal open={pending !== null} destructive={pending ? teamActionCopy[pending.action].destructive : false} title={pending ? teamActionCopy[pending.action].title : ''} body={pending ? `${teamActionCopy[pending.action].body(pending.member.name)} A ação exige a permissão Equipe · Gerir permissões, vale imediatamente e fica registrada na auditoria com o estado anterior e o novo.` : ''} confirmLabel={pending ? teamActionCopy[pending.action].confirm : 'Confirmar'} requireText={pending && teamActionCopy[pending.action].destructive ? 'CONFIRMAR' : undefined} onConfirm={confirmAction} onClose={() => setPending(null)} data-id="element-2166" data-mp="2go09mgmck9h6" />
    </div>;
}
export function AdminRoles() {
  const {
    tl
  } = useLocale();
  const navigate = useNavigate();
  return <div className="space-y-4" data-id="element-2167" data-mp="3j0nrnamh1nfw">
      <AdminPageTitle title="Papéis" subtitle="Cada papel concede um conjunto explícito de permissões por área." action={<Button variant="outline" onClick={() => navigate('/admin/papeis/matriz')} data-id="element-2169" data-mp="3lm5600z1djwj">
            Abrir matriz completa
          </Button>} data-id="element-2168" data-mp="3e3e1fsr9d7r0" />

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 xl:grid-cols-3" data-id="element-2170" data-mp="3cmi7po1lsu7p">
        {roles.map(role => {
        const granted = permissionMatrix[role];
        const areasWithAccess = permissionAreas.filter(area => (granted[area.id] ?? []).length > 0);
        return <Panel key={role} title={tl(roleLabels[role])} hint={`${areasWithAccess.length} de ${permissionAreas.length} áreas`} data-id="element-2171" data-mp="d7mu1o5z5k7n">
              <ul className="space-y-2" data-id="element-2172" data-mp="3h20if55r5vl">
                {areasWithAccess.map(area => <li key={area.id} data-id="element-2173" data-mp="r6wdqxte2wgd">
                    <p className="text-[12px] font-medium text-deep" data-id="element-2174" data-mp="r5tkij8lghxa">{tl(area.label)}</p>
                    <p className="mt-[2px] text-[11px] text-muted" data-id="element-2175" data-mp="vxushsyqbmhi">
                      {(granted[area.id] ?? []).map(permission => tl(permissionLabels[permission])).join(' · ')}
                    </p>
                  </li>)}
              </ul>
              {areasWithAccess.length === 0 && <p className="text-[12px] text-muted" data-id="element-2176" data-mp="7eqvfty76dd8">Sem permissões atribuídas.</p>}
              <p className="mt-3 border-t border-sand-line pt-2 text-[11px] leading-snug text-muted" data-id="element-2177" data-mp="3psqduqec4uz5">
                Dados sensíveis de saúde: não incluídos neste papel.
              </p>
            </Panel>;
      })}
      </div>

      <SensitiveHealthAccessPanel data-id="element-2178" data-mp="3fvlpcjx0onf6" />
    </div>;
}

/**
 * Sensitive health access sits OUTSIDE role inheritance: never granted by a
 * role (Super Admin included), only by an individual, justified, audited grant.
 */
function SensitiveHealthAccessPanel() {
  const {
    tl
  } = useLocale();
  const canIssue = canIssueSensitiveHealthGrant(prototypeAdminRole);
  const me = prototypeActorName();
  const eligible = team.filter(member => member.status === 'active' && member.name !== me);
  const [, refresh] = useState(0);
  const [recipient, setRecipient] = useState('');
  const [justification, setJustification] = useState('');
  const [confirmGrant, setConfirmGrant] = useState(false);
  const [revoking, setRevoking] = useState<SensitiveHealthGrant | null>(null);
  const alreadyGranted = sensitiveHealthGrants.some(grant => grant.grantee === recipient);
  const ready = recipient !== '' && justification.trim().length >= 10 && !alreadyGranted;
  return <Panel title="Acesso excepcional a dados sensíveis de saúde" hint="Permissão individual. Não faz parte de nenhum papel, nem do Super Admin." data-id="element-2179" data-mp="3653xqgeq1vtd">
      <div className="rounded-card border border-dashed border-gold/60 p-3" data-id="element-2180" data-mp="3fy1cqcki92n8">
        <p className="flex items-start gap-2 text-[12.5px] leading-relaxed text-deep" data-id="element-2181" data-mp="3tvps54vszhkw">
          <ShieldAlertIcon size={15} className="mt-[1px] shrink-0 text-gold-deep" aria-hidden="true" data-id="element-2182" data-mp="3oxy2khims7jv" />
          <span data-id="element-2183" data-mp="2mkt6kr9u4w0g">
            Biomarcadores, testosterona e composição corporal individuais só ficam visíveis com
            uma concessão individual. Cada concessão exige justificativa e registra na auditoria
            quem concedeu, para quem, quando e por quê. Desligada por padrão.
          </span>
        </p>
      </div>
      <p className="bee-overline mt-4 text-[8.5px] font-bold text-muted" data-id="element-2184" data-mp="30c6m7u4mh9q7">Concessões ativas</p>
      {sensitiveHealthGrants.length === 0 ? <p className="mt-1 text-[12px] text-muted" data-id="element-2185" data-mp="3a9ktp48kio6d">Nenhuma concessão individual ativa.</p> : <ul className="mt-1" data-id="element-2186" data-mp="3eb6cf67s9woh">
          {sensitiveHealthGrants.map(grant => <li key={grant.id} className="flex items-start justify-between gap-3 border-b border-sand-line py-2 last:border-b-0" data-id="element-2187" data-mp="3e93gy6nv3bcd">
              <span className="min-w-0" data-id="element-2188" data-mp="1jypxlh0x9a80">
                <span className="block text-[12.5px] font-bold text-deep" data-id="element-2189" data-mp="1ow9p3immrjhw">{grant.grantee}</span>
                <span className="block text-[11.5px] text-muted" data-id="element-2190" data-mp="1hcqltqpykthv">
                  Concedido por {grant.grantedBy} em {grant.grantedAt}
                </span>
                <span className="block text-[11.5px] text-muted" data-id="element-2191" data-mp="1ckfo8uioxjsh">Justificativa: {grant.justification}</span>
              </span>
              {canIssue && <Button variant="outline" onClick={() => setRevoking(grant)} data-id="element-2192" data-mp="12ocm3onu9l5j">
                  Revogar
                </Button>}
            </li>)}
        </ul>}

      <p className="bee-overline mt-4 text-[8.5px] font-bold text-muted" data-id="element-2193" data-mp="1vcrpfdxlyf11">Nova concessão individual</p>
      <PermissionGate allowed={canIssue} area="concessão de acesso a dados sensíveis de saúde" data-id="element-2194" data-mp="1qd5dd7tin4tt">
        <div className="mt-1 space-y-2" data-id="element-2195" data-mp="1mc3i122pzcx4">
          <label className="block" data-id="element-2196" data-mp="1mbaa3xs0yhde">
            <span className="block text-[11.5px] font-medium text-deep" data-id="element-2197" data-mp="1me6bjw32zo6l">Membro da equipe</span>
            <select value={recipient} onChange={event => setRecipient(event.target.value)} className="mt-1 h-10 w-full rounded-card border border-sand-line bg-white px-2 text-[12.5px] text-deep focus:border-deep/40 focus:outline-none" data-id="element-2198" data-mp="evx70gzb7en0">
              <option value="" data-id="element-2199" data-mp="23m5qi394gct2">Selecione</option>
              {eligible.map(member => <option key={member.id} value={member.name} data-id="element-2200" data-mp="1ot3pk1jnvj1l">
                  {member.name} · {member.role}
                </option>)}
            </select>
          </label>
          {alreadyGranted && <p role="alert" className="text-[11.5px] text-gold-deep" data-id="element-2201" data-mp="2mix7u753shuh">
              Este membro já tem uma concessão ativa.
            </p>}
          <label className="block" data-id="element-2202" data-mp="28ie4hdbl1zyn">
            <span className="block text-[11.5px] font-medium text-deep" data-id="element-2203" data-mp="28lie4q7thnwp">Justificativa (obrigatória)</span>
            <textarea rows={3} value={justification} onChange={event => setJustification(event.target.value)} placeholder="Ex.: atendimento a solicitação do próprio usuário, chamado #1234." className="mt-1 w-full rounded-card border border-sand-line bg-white p-2 text-[12.5px] leading-relaxed text-deep focus:border-deep/40 focus:outline-none" data-id="element-2204" data-mp="x40k4ig1g2nd" />
            <span className="block text-[11px] text-muted" data-id="element-2205" data-mp="13kiul6kqcek8">Mínimo de 10 caracteres.</span>
          </label>
          <p className="text-[11.5px] leading-snug text-muted" data-id="element-2206" data-mp="ym7liy29e832">
            {tl({
            'pt-BR': 'Será concedido por:',
            'en-US': 'Will be granted by:'
          })}{' '}
            <span className="font-bold text-deep" data-id="element-2207" data-mp="at8vitmhydg0">{me}</span>.{' '}
            {tl({
            'pt-BR': 'Data e hora são registradas na confirmação. Não é possível conceder para si mesmo, e conceder não dá acesso a quem concede.',
            'en-US': 'Date and time are recorded on confirmation. You cannot grant access to yourself, and granting does not give the grantor access.'
          })}
          </p>
          <Button variant="outline" disabled={!ready} onClick={() => setConfirmGrant(true)} data-id="element-2208" data-mp="3jac6zosdwgzh">
            Revisar concessão
          </Button>
        </div>
      </PermissionGate>

      <ConfirmationModal open={confirmGrant && canIssue} destructive title="Conceder acesso a dados sensíveis de saúde?" body={`${recipient} passará a ver biomarcadores, testosterona e composição corporal individuais. Justificativa: “${justification.trim()}”. Fica registrado na auditoria quem concedeu, para quem, quando e por quê.`} confirmLabel="Conceder acesso" requireText="CONFIRMAR" onConfirm={() => {
      if (!canIssue || !ready) return;
      grantSensitiveHealthAccess(recipient, justification.trim());
      setRecipient('');
      setJustification('');
      setConfirmGrant(false);
      refresh(v => v + 1);
    }} onClose={() => setConfirmGrant(false)} data-id="element-2209" data-mp="gv87pxsidaxf" />

      <ConfirmationModal open={revoking !== null && canIssue} destructive title="Revogar este acesso?" body={`${revoking?.grantee ?? ''} deixa de ver dados sensíveis de saúde imediatamente. A revogação fica registrada na auditoria.`} confirmLabel="Revogar acesso" onConfirm={() => {
      if (revoking && canIssue) revokeSensitiveHealthAccess(revoking.id);
      setRevoking(null);
      refresh(v => v + 1);
    }} onClose={() => setRevoking(null)} data-id="element-2210" data-mp="1edi25bk7pm21" />
    </Panel>;
}
export function AdminPermissionMatrix() {
  const {
    tl
  } = useLocale();
  const [role, setRole] = useState<TeamRole>('moderator');
  const [confirm, setConfirm] = useState(false);
  const granted = permissionMatrix[role];
  return <div className="space-y-4" data-id="element-2211" data-mp="2b3ua0k69dgql">
      <AdminPageTitle title="Matriz de permissões" subtitle="Permissões por área e por tipo de ação. Alterações exigem confirmação e ficam auditadas." action={<>
            <select value={role} onChange={event => setRole(event.target.value as TeamRole)} aria-label="Papel" className="h-10 rounded-card border border-sand-line bg-white px-2 text-[12.5px] font-medium text-deep focus:border-deep/40 focus:outline-none" data-id="element-2213" data-mp="4ddyw7ckqwkz">
              {roles.map(item => <option key={item} value={item} data-id="element-2214" data-mp="30km66k0202qw">
                  {tl(roleLabels[item])}
                </option>)}
            </select>
            <Button variant="gold" onClick={() => setConfirm(true)} data-id="element-2215" data-mp="2qmf0wyf4n94q">
              Salvar alterações
            </Button>
          </>} data-id="element-2212" data-mp="264irb5mc016d" />

      <div className="overflow-x-auto rounded-card border border-sand-line bg-white/80" data-id="element-2216" data-mp="2vjom21vhgymr">
        <table className="w-full min-w-[820px] border-collapse text-left" data-id="element-2217" data-mp="2hntakh3eypte">
          <thead data-id="element-2218" data-mp="2lod6jhc93ndy">
            <tr className="border-b border-sand-line" data-id="element-2219" data-mp="2lnvdsuosrras">
              <th scope="col" className="px-3 py-2" data-id="element-2220" data-mp="fu3ubxq715t9">
                <span className="bee-overline text-[8.5px] font-bold text-muted" data-id="element-2221" data-mp="fsivvx0rd2q4">Área</span>
              </th>
              {permissions.map(permission => <th key={permission} scope="col" className="px-3 py-2 text-center" data-id="element-2222" data-mp="24fawhi554tzf">
                  <span className="bee-overline text-[8.5px] font-bold text-muted" data-id="element-2223" data-mp="20dgzpky2ga4j">
                    {tl(permissionLabels[permission])}
                  </span>
                </th>)}
            </tr>
          </thead>
          <tbody data-id="element-2224" data-mp="29bt0ugwk6qb8">
            {permissionAreas.map(area => <tr key={area.id} className="border-b border-sand-line last:border-b-0" data-id="element-2225" data-mp="2o1h1rh0tvmyf">
                <th scope="row" className="px-3 py-2 text-[12.5px] font-medium text-deep" data-id="element-2226" data-mp="2j3oy5pqtmcee">
                  {tl(area.label)}
                </th>
                {permissions.map(permission => {
              const on = (granted[area.id] ?? []).includes(permission);
              return <td key={permission} className="px-3 py-2 text-center" data-id="element-2227" data-mp="12odyqr5kc15u">
                      <span className={`inline-grid h-7 w-7 place-items-center rounded-[8px] ${on ? 'bg-gold text-deep' : 'bg-sand-deep text-muted'}`} role="img" aria-label={`${tl(area.label)} · ${tl(permissionLabels[permission])}: ${on ? 'permitido' : 'não permitido'}`} data-id="element-2228" data-mp="xpub8mbbmhbd">
                        {on ? <CheckIcon size={14} strokeWidth={3} data-id="element-2229" data-mp="au6o5rqllw4n" /> : <MinusIcon size={13} data-id="element-2230" data-mp="3j7dndf88ldqa" />}
                      </span>
                    </td>;
            })}
              </tr>)}
          </tbody>
        </table>
      </div>

      <Panel title="Permissões especiais" hint="Concedidas por papel, além da matriz por área. Verificadas no backend." data-id="element-2231" data-mp="3s8tune3r8zqa">
        <ul data-id="element-2232" data-mp="3nblf7l9zr9sq">
          {capabilityOrder.map(capability => {
          const sensitive = capability === 'health.viewSensitive';
          // Never inherited from a role — always shown as off here.
          const on = !sensitive && capabilityMatrix[role].includes(capability);
          return <li key={capability} className="flex items-start gap-3 border-b border-sand-line py-2 last:border-b-0" data-id="element-2233" data-mp="1b2mw2jyk6key">
                <span className="min-w-0 flex-1" data-id="element-2234" data-mp="113nkasihy8nq">
                  <span className="flex items-center gap-2 text-[12.5px] font-medium text-deep" data-id="element-2235" data-mp="160xhiuc4nom4">
                    {tl(capabilityLabels[capability].label)}
                    {sensitive && <StatusBadge status="pending" label="Individual · fora dos papéis" data-id="element-2236" data-mp="1tsursgzxi8l4" />}
                  </span>
                  <span className="mt-[2px] block text-[11.5px] leading-snug text-muted" data-id="element-2237" data-mp="1k0yj6c07v02n">
                    {tl(capabilityLabels[capability].hint)}
                  </span>
                </span>
                <span className={`inline-grid h-7 w-7 shrink-0 place-items-center rounded-[8px] ${on ? 'bg-gold text-deep' : 'bg-sand-deep text-muted'}`} role="img" aria-label={`${tl(capabilityLabels[capability].label)}: ${on ? 'permitido' : 'não permitido'}`} data-id="element-2238" data-mp="1oxp174vl05qp">
                  {on ? <CheckIcon size={14} strokeWidth={3} data-id="element-2239" data-mp="2hdlzpjg8ugot" /> : <MinusIcon size={13} data-id="element-2240" data-mp="2hckamb2e7to3" />}
                </span>
              </li>;
        })}
        </ul>
      </Panel>

      <Panel title="Ações de alto impacto" hint="Cada uma exige a permissão explícita indicada, confirmação e registro na auditoria." data-id="element-2241" data-mp="2dewbevto3eqe">
        <ul data-id="element-2242" data-mp="23lyrcl911hpo">
          {highImpactActions.map(item => <li key={tl(item.action)} className="flex items-start justify-between gap-3 border-b border-sand-line py-2 text-[12.5px] last:border-b-0" data-id="element-2243" data-mp="1yndzb93xi4cb">
              <span className="font-medium text-deep" data-id="element-2244" data-mp="1pmpmuiu6oc1j">{tl(item.action)}</span>
              <span className="text-right text-muted" data-id="element-2245" data-mp="1posa4v3ybr2k">{tl(item.requires)}</span>
            </li>)}
        </ul>
      </Panel>

      <Panel title="Regra de segurança" data-id="element-2246" data-mp="6db9epdhabp">
        <p className="text-[12.5px] leading-relaxed text-muted" data-id="element-2247" data-mp="5c0lbc9qpui">
          Gerir permissões é a única ação que permite ampliar o próprio acesso, por isso
          pertence apenas ao Super Admin. Toda concessão e revogação é registrada na
          auditoria com o papel, a área, a permissão e o estado anterior.
        </p>
      </Panel>

      <ConfirmationModal open={confirm} title="Salvar alterações de permissão?" body="A mudança vale imediatamente para todos os membros com este papel e é registrada na auditoria." confirmLabel="Salvar" onConfirm={() => {
      recordAudit({
        action: {
          'pt-BR': 'Alterou permissões de papel',
          'en-US': 'Changed role permissions'
        },
        entity: `role:${role}`,
        context: {
          'pt-BR': 'Matriz de permissões',
          'en-US': 'Permission matrix'
        },
        severity: 'sensitive'
      });
      setConfirm(false);
    }} onClose={() => setConfirm(false)} data-id="element-2248" data-mp="3dkqall303hgw" />
    </div>;
}